import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Code, MessageSquare, FileText, Download, Users, Headphones, Rocket, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type DemoType = "document-summarization" | "code-translation" | "multiagent-qna" | "pdf-to-podcast" | "enterprise-rag-chatbot";

interface Blueprint {
  id: DemoType;
  title: string;
  description: string;
  category: string;
  icon: typeof MessageSquare;
  complexity: string;
  features: string[];
}

const Blueprints = () => {
  const { toast } = useToast();
  
  // Modal state
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [activeDemoType, setActiveDemoType] = useState<DemoType | null>(null);
  
  // Form state
  const [inferenceUrl, setInferenceUrl] = useState("");
  const [model, setModel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [embeddingModel, setEmbeddingModel] = useState("bge-base-en-v1.5");
  
  // Keycloak state
  const [useKeycloak, setUseKeycloak] = useState(false);
  const [keycloakClientId, setKeycloakClientId] = useState("");
  const [keycloakClientSecret, setKeycloakClientSecret] = useState("");
  const [keycloakRealm, setKeycloakRealm] = useState("master");

  // TTS-specific state for PDF-to-Podcast
  const [useSameEndpointForTts, setUseSameEndpointForTts] = useState(true);
  const [ttsApiKey, setTtsApiKey] = useState("");
  const [ttsBaseUrl, setTtsBaseUrl] = useState("https://api.openai.com/v1");
  const [ttsModel, setTtsModel] = useState("tts-1-hd");
  const [hostVoice, setHostVoice] = useState("alloy");
  const [guestVoice, setGuestVoice] = useState("nova");
  const [useTtsKeycloak, setUseTtsKeycloak] = useState(false);
  const [ttsKeycloakClientId, setTtsKeycloakClientId] = useState("");
  const [ttsKeycloakClientSecret, setTtsKeycloakClientSecret] = useState("");
  const [ttsKeycloakAudience, setTtsKeycloakAudience] = useState("");

  // RAG Chatbot embeddings configuration
  const [useLocalEmbeddings, setUseLocalEmbeddings] = useState(false);
  const [embeddingsBaseUrl, setEmbeddingsBaseUrl] = useState("");

  const blueprints: Blueprint[] = [
    {
      id: "enterprise-rag-chatbot",
      title: "Enterprise RAG Chatbot",
      description: "Intelligent customer service chatbot with RAG and context awareness for enterprise knowledge bases",
      category: "Conversational AI",
      icon: MessageSquare,
      complexity: "Medium",
      features: ["Document ingestion", "Context-aware responses", "Local/remote embeddings"]
    },
    {
      id: "code-translation",
      title: "Code Translation",
      description: "AI-powered code translation between programming languages with syntax highlighting",
      category: "Developer Tools",
      icon: Code,
      complexity: "High",
      features: ["Multi-language support", "Syntax preservation", "Code optimization"]
    },
    {
      id: "document-summarization",
      title: "Document Summarization",
      description: "AI-powered document summarization with customizable deployment options",
      category: "Document Processing",
      icon: FileText,
      complexity: "Medium",
      features: ["Multi-format support", "Extractive summaries", "Key point extraction"]
    },
    {
      id: "multiagent-qna",
      title: "Multi-Agent Q&A",
      description: "Multi-agent question and answering system with RAG and embeddings support",
      category: "Conversational AI",
      icon: Users,
      complexity: "High",
      features: ["Agent orchestration", "RAG integration", "Knowledge synthesis"]
    },
    {
      id: "pdf-to-podcast",
      title: "PDF to Podcast",
      description: "Convert PDF documents into engaging podcast-style audio conversations",
      category: "Audio Generation",
      icon: Headphones,
      complexity: "High",
      features: ["TTS integration", "Multi-voice support", "Script generation"]
    }
  ];

  const generateDockerCompose = (demoType: DemoType) => {
    const keycloakEnv = useKeycloak 
      ? `KEYCLOAK_CLIENT_ID: "${keycloakClientId}"
      KEYCLOAK_CLIENT_SECRET: "${keycloakClientSecret}"
      KEYCLOAK_REALM: "${keycloakRealm}"`
      : `KEYCLOAK_CLIENT_ID: ""
      KEYCLOAK_CLIENT_SECRET: ""
      KEYCLOAK_REALM: "master"`;

    if (demoType === "code-translation") {
      return `services:
  backend:
    image: vectorpathconsulting/code-translation-backend:latest
    container_name: code-translation-backend
    environment:
      BASE_URL: "${inferenceUrl}"
      INFERENCE_API_KEY: "${apiKey}"
      INFERENCE_MODEL_NAME: "${model}"
      ${keycloakEnv}
      LLM_TEMPERATURE: "0.2"
      LLM_MAX_TOKENS: "4096"
      MAX_CODE_LENGTH: "10000"
      MAX_FILE_SIZE: "10485760"
    ports:
      - "5001:5001"
    networks:
      - appnet
    restart: unless-stopped

  frontend:
    image: vectorpathconsulting/code-translation-frontend:latest
    container_name: code-translation-frontend
    depends_on:
      - backend
    ports:
      - "3000:80"
    networks:
      - appnet
    restart: unless-stopped

  info:
    image: alpine:latest
    container_name: code-translation-info
    command: >
      sh -c '
      echo "";
      echo "==============================================";
      echo "   Inference Blueprint: Code Translation";
      echo "----------------------------------------------";
      echo "   Open your browser to: http://localhost:3000";
      echo "==============================================";
      echo "";
      sleep 8;
      '
    networks:
      - appnet
    restart: "no"

networks:
  appnet:
    driver: bridge
`;
    }

    if (demoType === "multiagent-qna") {
      return `services:
  backend:
    image: vectorpathconsulting/multiagent-qna-backend:latest
    container_name: multiagent-qna-backend
    environment:
      INFERENCE_API_KEY: "${apiKey}"
      BASE_URL: "${inferenceUrl}"
      EMBEDDING_MODEL_NAME: "${embeddingModel}"
      INFERENCE_MODEL_NAME: "${model}"
      ${keycloakEnv}
      LLM_TEMPERATURE: "0.2"
      LLM_MAX_TOKENS: "4096"
    ports:
      - "8002:5001"
    networks:
      - appnet
    restart: unless-stopped

  frontend:
    image: vectorpathconsulting/multiagent-qna-frontend:latest
    container_name: multiagent-qna-frontend
    depends_on:
      - backend
    ports:
      - "8082:3000"
    networks:
      - appnet
    restart: unless-stopped

  info:
    image: alpine:latest
    container_name: multiagent-qna-info
    command: >
      sh -c "
      echo '';
      echo '==============================================';
      echo '  Inference Blueprint: Multi-Agent Q&A';
      echo '----------------------------------------------';
      echo '  Open your browser to: http://localhost:8082';
      echo '==============================================';
      echo '';
      sleep 10;
      "
    networks:
      - appnet
    restart: "no"

networks:
  appnet:
    driver: bridge
`;
    }

    if (demoType === "pdf-to-podcast") {
      const llmKeycloakEnv = useKeycloak 
        ? `KEYCLOAK_CLIENT_ID: "${keycloakClientId}"
      KEYCLOAK_CLIENT_SECRET: "${keycloakClientSecret}"
      KEYCLOAK_REALM: "${keycloakRealm}"`
        : `KEYCLOAK_CLIENT_ID: ""
      KEYCLOAK_CLIENT_SECRET: ""
      KEYCLOAK_REALM: "master"`;

      const effectiveTtsBaseUrl = useSameEndpointForTts ? inferenceUrl : ttsBaseUrl;
      const effectiveTtsApiKey = useSameEndpointForTts ? apiKey : ttsApiKey;

      const ttsKeycloakEnv = (!useSameEndpointForTts && useTtsKeycloak)
        ? `KEYCLOAK_CLIENT_ID: "${ttsKeycloakClientId}"
      KEYCLOAK_CLIENT_SECRET: "${ttsKeycloakClientSecret}"
      KEYCLOAK_AUDIENCE: "${ttsKeycloakAudience}"`
        : `KEYCLOAK_CLIENT_ID: ""
      KEYCLOAK_CLIENT_SECRET: ""
      KEYCLOAK_AUDIENCE: ""`;

      return `version: "3.9"

services:
  pdf-service:
    image: vectorpathconsulting/pdf-podcast-pdf-service:latest
    container_name: pdf-podcast-pdf-service
    ports:
      - "8001:8001"
    networks:
      - appnet
    restart: unless-stopped

  llm-service:
    image: vectorpathconsulting/pdf-podcast-llm-service:latest
    container_name: pdf-podcast-llm-service
    environment:
      BASE_URL: "${inferenceUrl}"
      INFERENCE_API_KEY: "${apiKey}"
      ${llmKeycloakEnv}
      INFERENCE_MODEL_NAME: "${model}"
    ports:
      - "8002:8002"
    networks:
      - appnet
    restart: unless-stopped

  tts-service:
    image: vectorpathconsulting/pdf-podcast-tts-service:latest
    container_name: pdf-podcast-tts-service
    environment:
      TTS_BASE_URL: "${effectiveTtsBaseUrl}"
      TTS_API_KEY: "${effectiveTtsApiKey}"
      ${ttsKeycloakEnv}
      TTS_MODEL: "${ttsModel}"
      DEFAULT_HOST_VOICE: "${hostVoice}"
      DEFAULT_GUEST_VOICE: "${guestVoice}"
      OUTPUT_DIR: "/app/static/audio"
    ports:
      - "8003:8003"
    networks:
      - appnet
    restart: unless-stopped

  backend:
    image: vectorpathconsulting/pdf-podcast-backend:latest
    container_name: pdf-podcast-backend
    environment:
      PDF_SERVICE_URL: "http://pdf-service:8001"
      LLM_SERVICE_URL: "http://llm-service:8002"
      TTS_SERVICE_URL: "http://tts-service:8003"
    depends_on:
      - pdf-service
      - llm-service
      - tts-service
    ports:
      - "8000:8000"
    networks:
      - appnet
    restart: unless-stopped

  frontend:
    image: vectorpathconsulting/pdf-podcast-frontend:latest
    container_name: pdf-podcast-frontend
    depends_on:
      - backend
    ports:
      - "8083:3000"
    networks:
      - appnet
    restart: unless-stopped

  info:
    image: alpine:latest
    container_name: pdf-podcast-info
    command: >
      sh -c "
      echo '';
      echo '==============================================';
      echo '   Inference Blueprint: PDF to Podcast';
      echo '----------------------------------------------';
      echo '   Open your browser to: http://localhost:8083';
      echo '==============================================';
      echo '';
      sleep 10;
      "
    networks:
      - appnet

networks:
  appnet:
    driver: bridge
`;
    }

    if (demoType === "enterprise-rag-chatbot") {
      if (useLocalEmbeddings) {
        return `version: "3.9"

services:
  rag-backend:
    image: vectorpathconsulting/rag-chat-backend:latest
    container_name: rag-chat-backend
    ports:
      - "5001:5001"
    environment:
      BASE_URL: "${inferenceUrl}"
      EMBEDDINGS_BASE_URL: "http://local-embeddings:8080"
      INFERENCE_API_KEY: "${apiKey}"
      INFERENCE_MODEL_NAME: "${model}"
      EMBEDDING_MODEL_NAME: "${embeddingModel}"
    networks:
      - appnet
    restart: unless-stopped
    depends_on:
      - local-embeddings

  rag-frontend:
    image: vectorpathconsulting/rag-chat-frontend:latest
    container_name: rag-chat-frontend
    depends_on:
      - rag-backend
    environment:
      VITE_API_URL: "/api"
      BACKEND_PROXY_URL: "http://rag-backend:5001"
    ports:
      - "8084:3000"
    networks:
      - appnet
    restart: unless-stopped

  local-embeddings:
    image: vectorpathconsulting/rag-local-embeddings:latest
    container_name: rag-local-embeddings
    environment:
      MODEL_NAME: "${embeddingModel}"
    ports:
      - "8088:8080"
    networks:
      - appnet
    restart: unless-stopped
    healthcheck:
      test: ["CMD", "curl", "-f", "http://localhost:8080/health"]
      interval: 10s
      timeout: 5s
      retries: 5
      start_period: 10s

  info:
    image: alpine:latest
    container_name: rag-chat-info
    command: >
      sh -c "
      echo '';
      echo '==============================================';
      echo '   Inference Blueprint: RAG Chatbot';
      echo '----------------------------------------------';
      echo '   API:      http://localhost:5001';
      echo '   Frontend: http://localhost:8084';
      echo '   Local embeddings: http://localhost:8088';
      echo '==============================================';
      echo '';
      sleep 10;
      "
    networks:
      - appnet

networks:
  appnet:
    driver: bridge
`;
      } else {
        return `version: "3.9"

services:
  rag-backend:
    image: vectorpathconsulting/rag-chat-backend:latest
    container_name: rag-chat-backend
    ports:
      - "5001:5001"
    environment:
      BASE_URL: "${inferenceUrl}"
      EMBEDDINGS_BASE_URL: "${embeddingsBaseUrl}"
      INFERENCE_API_KEY: "${apiKey}"
      INFERENCE_MODEL_NAME: "${model}"
      EMBEDDING_MODEL_NAME: "${embeddingModel}"
    networks:
      - appnet
    restart: unless-stopped

  rag-frontend:
    image: vectorpathconsulting/rag-chat-frontend:latest
    container_name: rag-chat-frontend
    depends_on:
      - rag-backend
    environment:
      VITE_API_URL: "/api"
      BACKEND_PROXY_URL: "http://rag-backend:5001"
    ports:
      - "8084:3000"
    networks:
      - appnet
    restart: unless-stopped

  info:
    image: alpine:latest
    container_name: rag-chat-info
    command: >
      sh -c "
      echo '';
      echo '==============================================';
      echo '   Inference Blueprint: RAG Chatbot';
      echo '----------------------------------------------';
      echo '   API:      http://localhost:5001';
      echo '   Frontend: http://localhost:8084';
      echo '==============================================';
      echo '';
      sleep 10;
      "
    networks:
      - appnet

networks:
  appnet:
    driver: bridge
`;
      }
    }

    // Document Summarization
    return `services:
  backend:
    image: vectorpathconsulting/doc-summarization-backend:latest
    container_name: doc-summarization-backend
    environment:
      BASE_URL: "${inferenceUrl}"
      INFERENCE_API_KEY: "${apiKey}"
      INFERENCE_MODEL_NAME: "${model}"
      ${keycloakEnv}
      LLM_TEMPERATURE: "0.7"
      LLM_MAX_TOKENS: "2000"
    ports:
      - "8000:8000"
    networks:
      - appnet
    restart: unless-stopped

  frontend:
    image: vectorpathconsulting/doc-summarization-frontend:latest
    container_name: doc-summarization-frontend
    depends_on:
      - backend
    ports:
      - "8080:80"
    networks:
      - appnet
    restart: unless-stopped

  info:
    image: alpine:latest
    container_name: doc-sum-info
    command: >
      sh -c '
      echo "";
      echo "==============================================";
      echo "   Inference Blueprint: Document Summarization";
      echo "----------------------------------------------";
      echo "   Open your browser to: http://localhost:8080";
      echo "==============================================";
      echo "";
      sleep 8;
      '
    networks:
      - appnet
    restart: "no"

networks:
  appnet:
    driver: bridge
`;
  };

  const handleDownload = () => {
    if (!activeDemoType) return;
    const yaml = generateDockerCompose(activeDemoType);
    const blob = new Blob([yaml], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "docker-compose.yml";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDownloadModalOpen(false);
    resetForm();
    toast({
      title: "Download started",
      description: "Run 'docker compose up' to start your application.",
    });
  };

  const resetForm = () => {
    setInferenceUrl("");
    setModel("");
    setApiKey("");
    setEmbeddingModel("bge-base-en-v1.5");
    setUseKeycloak(false);
    setKeycloakClientId("");
    setKeycloakClientSecret("");
    setKeycloakRealm("master");
    setUseSameEndpointForTts(true);
    setTtsApiKey("");
    setTtsBaseUrl("https://api.openai.com/v1");
    setTtsModel("tts-1-hd");
    setHostVoice("alloy");
    setGuestVoice("nova");
    setUseTtsKeycloak(false);
    setTtsKeycloakClientId("");
    setTtsKeycloakClientSecret("");
    setTtsKeycloakAudience("");
    setUseLocalEmbeddings(false);
    setEmbeddingsBaseUrl("");
    setActiveDemoType(null);
  };

  const copyLlmToEmbeddings = () => {
    setEmbeddingsBaseUrl(inferenceUrl);
  };

  const openDownloadModal = (demoType: DemoType) => {
    resetForm();
    setActiveDemoType(demoType);
    if (demoType === "multiagent-qna") {
      setModel("meta-llama/Llama-3.1-8B-Instruct");
      setEmbeddingModel("bge-base-en-v1.5");
    }
    if (demoType === "enterprise-rag-chatbot") {
      setEmbeddingModel("BAAI/bge-base-en-v1.5");
    }
    setDownloadModalOpen(true);
  };

  const getModalTitle = () => {
    switch (activeDemoType) {
      case "code-translation": return "Configure Code Translation";
      case "document-summarization": return "Configure Document Summarization";
      case "multiagent-qna": return "Configure Multi-Agent Q&A";
      case "pdf-to-podcast": return "Configure PDF to Podcast";
      case "enterprise-rag-chatbot": return "Configure Enterprise RAG Chatbot";
      default: return "Configure Deployment";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      {/* Hero Section */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-primary/5" />
        <div className="container mx-auto px-4 py-16 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
              <Rocket className="h-4 w-4" />
              <span className="text-sm font-medium">Beta Access</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              AI Inference Blueprints
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Deploy production-ready AI applications in minutes with Docker Compose. 
              Configure your infrastructure, download, and run.
            </p>
            <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span>Pre-built containers</span>
              </div>
              <div className="flex items-center gap-2">
                <Download className="h-4 w-4 text-primary" />
                <span>One-click download</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Blueprints Grid */}
      <main className="container mx-auto px-4 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blueprints.map((blueprint) => {
              const IconComponent = blueprint.icon;
              return (
                <Card 
                  key={blueprint.id} 
                  className="group relative overflow-hidden border-border/50 hover:border-primary/30 hover:shadow-lg transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <CardHeader className="relative">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center shrink-0">
                        <IconComponent className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg mb-1">{blueprint.title}</CardTitle>
                        <Badge variant="secondary" className="text-xs">
                          {blueprint.category}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="relative">
                    <CardDescription className="mb-4 line-clamp-2">
                      {blueprint.description}
                    </CardDescription>
                    
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {blueprint.features.map((feature) => (
                        <span 
                          key={feature}
                          className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between pt-2 border-t border-border/50">
                      <span className="text-sm text-muted-foreground">
                        Complexity: <span className="font-medium text-foreground">{blueprint.complexity}</span>
                      </span>
                      <Button 
                        size="sm"
                        className="gap-2"
                        onClick={() => openDownloadModal(blueprint.id)}
                      >
                        <Download className="h-4 w-4" />
                        Download
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 py-8">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>Powered by VectorPath Consulting</p>
        </div>
      </footer>

      {/* Download Configuration Modal */}
      <Dialog open={downloadModalOpen} onOpenChange={setDownloadModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{getModalTitle()}</DialogTitle>
            <DialogDescription>
              Enter your infrastructure details to generate a docker-compose.yml file.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* LLM Section Header for PDF-to-Podcast */}
            {activeDemoType === "pdf-to-podcast" && (
              <div className="pb-2 border-b">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">LLM Service</h3>
              </div>
            )}

            {/* Common LLM fields */}
            <div className="space-y-2">
              <Label htmlFor="inferenceUrl">Inference Base URL</Label>
              <Input
                id="inferenceUrl"
                placeholder="https://your-inference-endpoint/v1"
                value={inferenceUrl}
                onChange={(e) => setInferenceUrl(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="model">Model Name</Label>
              <Input
                id="model"
                placeholder="meta-llama/Llama-3.1-8B-Instruct"
                value={model}
                onChange={(e) => setModel(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="apiKey">API Key</Label>
              <Input
                id="apiKey"
                type="password"
                placeholder="Your API key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
            </div>

            {/* Keycloak toggle */}
            <div className="flex items-center justify-between py-2">
              <Label htmlFor="useKeycloak" className="cursor-pointer">Use Keycloak Authentication</Label>
              <Switch
                id="useKeycloak"
                checked={useKeycloak}
                onCheckedChange={setUseKeycloak}
              />
            </div>
            
            {useKeycloak && (
              <div className="space-y-3 pl-4 border-l-2 border-primary/20">
                <div className="space-y-2">
                  <Label htmlFor="keycloakClientId">Client ID</Label>
                  <Input
                    id="keycloakClientId"
                    placeholder="your-client-id"
                    value={keycloakClientId}
                    onChange={(e) => setKeycloakClientId(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="keycloakClientSecret">Client Secret</Label>
                  <Input
                    id="keycloakClientSecret"
                    type="password"
                    placeholder="your-client-secret"
                    value={keycloakClientSecret}
                    onChange={(e) => setKeycloakClientSecret(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="keycloakRealm">Realm</Label>
                  <Input
                    id="keycloakRealm"
                    placeholder="master"
                    value={keycloakRealm}
                    onChange={(e) => setKeycloakRealm(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* RAG Chatbot specific fields */}
            {activeDemoType === "enterprise-rag-chatbot" && (
              <>
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Embeddings Configuration</h3>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <Label htmlFor="useLocalEmbeddings" className="cursor-pointer">Use Local Embeddings Container</Label>
                  <Switch
                    id="useLocalEmbeddings"
                    checked={useLocalEmbeddings}
                    onCheckedChange={setUseLocalEmbeddings}
                  />
                </div>

                {!useLocalEmbeddings && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="embeddingsBaseUrl">Embeddings Base URL</Label>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={copyLlmToEmbeddings}
                        className="text-xs h-6"
                      >
                        Copy from LLM
                      </Button>
                    </div>
                    <Input
                      id="embeddingsBaseUrl"
                      placeholder="https://your-embeddings-endpoint/v1"
                      value={embeddingsBaseUrl}
                      onChange={(e) => setEmbeddingsBaseUrl(e.target.value)}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="embeddingModel">Embedding Model Name</Label>
                  <Input
                    id="embeddingModel"
                    placeholder="BAAI/bge-base-en-v1.5"
                    value={embeddingModel}
                    onChange={(e) => setEmbeddingModel(e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Multi-Agent Q&A embeddings field */}
            {activeDemoType === "multiagent-qna" && (
              <div className="space-y-2">
                <Label htmlFor="embeddingModel">Embedding Model Name</Label>
                <Input
                  id="embeddingModel"
                  placeholder="bge-base-en-v1.5"
                  value={embeddingModel}
                  onChange={(e) => setEmbeddingModel(e.target.value)}
                />
              </div>
            )}

            {/* PDF-to-Podcast TTS fields */}
            {activeDemoType === "pdf-to-podcast" && (
              <>
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">TTS Service</h3>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <Label htmlFor="useSameEndpoint" className="cursor-pointer">Use same endpoint as LLM</Label>
                  <Switch
                    id="useSameEndpoint"
                    checked={useSameEndpointForTts}
                    onCheckedChange={setUseSameEndpointForTts}
                  />
                </div>

                {!useSameEndpointForTts && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="ttsBaseUrl">TTS Base URL</Label>
                      <Input
                        id="ttsBaseUrl"
                        placeholder="https://api.openai.com/v1"
                        value={ttsBaseUrl}
                        onChange={(e) => setTtsBaseUrl(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ttsApiKey">TTS API Key</Label>
                      <Input
                        id="ttsApiKey"
                        type="password"
                        placeholder="Your TTS API key"
                        value={ttsApiKey}
                        onChange={(e) => setTtsApiKey(e.target.value)}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between py-2">
                      <Label htmlFor="useTtsKeycloak" className="cursor-pointer">TTS Keycloak Auth</Label>
                      <Switch
                        id="useTtsKeycloak"
                        checked={useTtsKeycloak}
                        onCheckedChange={setUseTtsKeycloak}
                      />
                    </div>
                    
                    {useTtsKeycloak && (
                      <div className="space-y-3 pl-4 border-l-2 border-primary/20">
                        <div className="space-y-2">
                          <Label htmlFor="ttsKeycloakClientId">TTS Client ID</Label>
                          <Input
                            id="ttsKeycloakClientId"
                            placeholder="tts-client-id"
                            value={ttsKeycloakClientId}
                            onChange={(e) => setTtsKeycloakClientId(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="ttsKeycloakClientSecret">TTS Client Secret</Label>
                          <Input
                            id="ttsKeycloakClientSecret"
                            type="password"
                            placeholder="tts-client-secret"
                            value={ttsKeycloakClientSecret}
                            onChange={(e) => setTtsKeycloakClientSecret(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="ttsKeycloakAudience">TTS Audience</Label>
                          <Input
                            id="ttsKeycloakAudience"
                            placeholder="tts-audience"
                            value={ttsKeycloakAudience}
                            onChange={(e) => setTtsKeycloakAudience(e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="space-y-2">
                  <Label htmlFor="ttsModel">TTS Model</Label>
                  <Input
                    id="ttsModel"
                    placeholder="tts-1-hd"
                    value={ttsModel}
                    onChange={(e) => setTtsModel(e.target.value)}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="hostVoice">Host Voice</Label>
                    <Input
                      id="hostVoice"
                      placeholder="alloy"
                      value={hostVoice}
                      onChange={(e) => setHostVoice(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guestVoice">Guest Voice</Label>
                    <Input
                      id="guestVoice"
                      placeholder="nova"
                      value={guestVoice}
                      onChange={(e) => setGuestVoice(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDownloadModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleDownload} className="gap-2">
              <Download className="h-4 w-4" />
              Download docker-compose.yml
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Blueprints;
